import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import CreateUpdateGuardCommon from "./CreateUpdateGuardCommon";
import {UploadImage} from "../../../../reduxToolkit/ShareSlice/shareApi";
import {HardDeleteRecordsByTableAndIds} from "../../../../reduxToolkit/Commons/CommonsApi";
import {AddGuardDocument, GetZoneAccessByGuard, UpdateGuardUser} from "../../../../reduxToolkit/Guards/GuardsApi";
import {accessMethodModel} from "../../../../enums/accessMethodEnum";
import {TABLES} from "../../../../Apis/Tables";
import {downloadActionFile, errorMsg} from "../../../../Apis/functions";
import {URL} from "../../../../Apis/Constants";
import {getPhotoPicker} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import {GUARD_ROSTER_PERMISSION, guardDocumentPath} from "../Model/Model";

/**
 * Profile of a guard. Its details, contract, photo and the documents added are saved together;
 * without the update permission the guard is only read, as its details.
 */
const UpdateGuardApp = () => {
    const {
        t, navigate, DEV, permission, dispatch, activeStep, setActiveStep, handleStepChange,
        loadingProgress, setLoadingProgress, capturing, setCapturing, handleCloseModal, userData,
        setUserData, handleFormChangeUserData, emptyFields, setEmptyFields, isValidEmail,
        isValidDocumentId, isValidPhoneNumber, isValidDob, setIsValidDob, isValidPin,
        isValidPassword, employeeData, setEmployeeData, handleFormChangeEmployeeData,
        isValidEndDate, setIsValidEndDate, zoneAccesses, zoneAccessIds, setZoneAccessIds, documents,
        getDocuments, addDocument, removeDocument, photoPickerFile, setPhotoPickerFile,
        savedSelfieId, emptySelfie, isPilgrimsValidation, selectedReader, setSelectedReader,
        availableReaders, setAvailableReaders, getAvailableReaders, capturedFingerprint,
        setCapturedFingerprint, fingerprintSrc, setFingerprintSrc, fingerprintsList,
        fingerprintsTemp, setFingerprintsTemp, mergedFingerprints, handleUploadFinger, fingerprintName, setFingerprintName, updated, setUpdated,
        updatedFinger, setUpdatedFinger, setModified, assignZoneAccess, uploadUserImage,
        secretModal, setSecretModal, secretKey, setSecretKey,
    } = CreateUpdateGuardCommon();
    // The zone accesses the guard had when it was loaded, they are only assigned again when they change
    const [savedZoneAccessIds, setSavedZoneAccessIds] = useState([]);

    const canUpdate = DEV || permission?.includes(GUARD_ROSTER_PERMISSION.UPDATE);
    // Without the update permission, a guard is only read
    const canRead = DEV || permission?.includes(GUARD_ROSTER_PERMISSION.READ);

    // The zone accesses and the documents of a guard are read once its employee data is loaded
    useEffect(() => {
        if (!userData?.id) return;
        dispatch(GetZoneAccessByGuard(userData.id)).then(({payload: {data: {data, success}}}) => {
            if (!success) return;
            const ids = (data || []).map((zoneAccess) => zoneAccess.id);
            setZoneAccessIds(ids);
            setSavedZoneAccessIds(ids);
        });
        getDocuments(userData.id);
    }, [userData?.id]);

    /**
     * Uploads the documents added since the guard was loaded.
     */
    const savePendingDocuments = () => {
        const pending = documents.filter((document) => document.file);
        if (pending.length === 0) return;
        Promise.all(pending.map((document) =>
            dispatch(AddGuardDocument({userId: userData.id, name: document.name, file: document.file}))
        )).then((responses) => {
            const failed = responses.find(({payload}) => !payload?.data?.success);
            if (failed) errorMsg(failed.payload?.data?.code);
            getDocuments(userData.id);
        });
    };

    /**
     * Downloads the file of a saved document from its path in the file upload service.
     */
    const downloadDocument = (document) => downloadActionFile(URL + guardDocumentPath(document));

    /**
     * Saves the details and the contract of the guard, and the zone accesses it is assigned to.
     */
    const handleUpdateGuard = () => {
        const body = {
            employee: employeeData.set(),
            user: userData.set(),
        };

        return dispatch(UpdateGuardUser(body)).then(({payload: {data: {data, success, code}}}) => {
            if (!success) {
                errorMsg(code);
                return;
            }
            const zoneAccessesChanged = zoneAccessIds.length !== savedZoneAccessIds.length
                || zoneAccessIds.some((zoneAccessId) => !savedZoneAccessIds.includes(zoneAccessId));
            if (zoneAccessesChanged) {
                assignZoneAccess(userData.id);
                setSavedZoneAccessIds(zoneAccessIds);
            }
            if (userData.password) {
                setSecretKey(data?.user?.secret);
                setSecretModal(true);
            }
            toast.success(t('has_been_updated_successfully'));
        });
    };

    /**
     * Replaces the selfie of the guard, uploads it when it had none, or deletes it when it was removed
     * from the picker, as the one of an employee. It is saved with the details, so the success is shown
     * once by the update of the guard.
     */
    const handleUpdatePhoto = () => {
        const imageProfile = getPhotoPicker(photoPickerFile);
        if (!imageProfile) {
            if (!savedSelfieId || photoPickerFile?.id) return Promise.resolve();
            return dispatch(HardDeleteRecordsByTableAndIds({tableName: TABLES.USER_IMAGE, body: [savedSelfieId]}))
                .then(({payload: {data: {success, code}}}) => {
                    if (!success) errorMsg(code);
                });
        }
        if (photoPickerFile?.id) {
            const formData = new FormData();
            formData.append('id', photoPickerFile.id);
            formData.append('option', TABLES.USER);
            formData.append('file', imageProfile);
            return dispatch(UploadImage(formData)).then(({payload: {data: {success, code}}}) => {
                if (!success) errorMsg(code);
            });
        }
        return uploadUserImage(userData.id, accessMethodModel.FACE_RECOGNITION.id, 'Face recognition', imageProfile);
    };

    // Without the update permission the guard is only read
    const disabled = !canUpdate;

    return {
        t,
        navigate,
        canUpdate,
        canRead,
        disabled,
        activeStep,
        setActiveStep,
        handleStepChange,
        loadingProgress,
        setLoadingProgress,
        capturing,
        setCapturing,
        handleCloseModal,
        userData,
        setUserData,
        handleFormChangeUserData,
        emptyFields,
        setEmptyFields,
        isValidEmail,
        isValidDocumentId,
        isValidPhoneNumber,
        isValidDob,
        setIsValidDob,
        isValidPin,
        isValidPassword,
        employeeData,
        setEmployeeData,
        handleFormChangeEmployeeData,
        isValidEndDate,
        setIsValidEndDate,
        zoneAccesses,
        zoneAccessIds,
        setZoneAccessIds,
        documents,
        addDocument,
        removeDocument,
        downloadDocument,
        savePendingDocuments,
        photoPickerFile,
        setPhotoPickerFile,
        emptySelfie,
        isPilgrimsValidation,
        selectedReader,
        setSelectedReader,
        availableReaders,
        setAvailableReaders,
        getAvailableReaders,
        capturedFingerprint,
        setCapturedFingerprint,
        fingerprintSrc,
        setFingerprintSrc,
        fingerprintsList,
        fingerprintsTemp,
        setFingerprintsTemp,
        mergedFingerprints,
        handleUploadFinger,
        fingerprintName,
        setFingerprintName,
        updated,
        setUpdated,
        updatedFinger,
        setUpdatedFinger,
        setModified,
        handleUpdateGuard,
        handleUpdatePhoto,
        secretModal,
        setSecretModal,
        secretKey,
        setSecretKey,
    };
};

export default UpdateGuardApp;
